import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy, getDocs,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/Modal';
import { PortalPreviewFrame, supplierViewer, useSupplierLoginEmails } from '../../components/PortalPreview';
import { rebuildAllSupplierViews } from '../../utils/supplierViewRequests';
import {
  CATEGORY_DEFAULTS, CATEGORY_PRESET_LABELS,
  PERMISSION_LABELS, PERMISSION_HINTS, PERMISSION_GROUPS, DEFAULT_PERMISSIONS, effectivePermissions,
} from '../../utils/supplierPermissions';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  address: '',
  googleLink: '',
  locationName: '',
  serviceTypes: [],
  notes: '',
  permissions: { ...DEFAULT_PERMISSIONS },
  category: '',
  emailLanguage: 'es',
};

// Which language this supplier's emails go out in (functions: supplierWantsSpanish).
function EmailLanguageField({ value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px]">Email language</label>
      <div className="flex rounded-[9px] border border-[#E5E7EB] overflow-hidden">
        {[['es', 'Español'], ['en', 'English']].map(([key, label]) => (
          <button key={key} type="button" onClick={() => onChange(key)}
            className={`px-[12px] py-[7px] text-[12.5px] font-semibold transition ${value === key ? 'bg-navy text-white' : 'bg-white text-[#374151] hover:bg-surface'}`}>
            {label}
          </button>
        ))}
      </div>
      <span className="text-[11.5px] text-[#9CA3AF]">Booking, approval, food order and tour update emails</span>
    </div>
  );
}

function PermissionsEditor({ permissions, onChange, category, onCategoryChange }) {
  function applyPreset() {
    if (category && CATEGORY_DEFAULTS[category]) {
      onChange({ ...CATEGORY_DEFAULTS[category] });
    }
  }

  function toggle(key) {
    onChange({ ...permissions, [key]: !permissions[key] });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">
            Apply category defaults
          </label>
          <select
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
          >
            <option value="">Select a category…</option>
            {Object.entries(CATEGORY_PRESET_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={applyPreset}
          disabled={!category}
          className="px-[14px] py-[9px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-40 whitespace-nowrap"
        >
          Apply defaults
        </button>
      </div>

      {PERMISSION_GROUPS.map((group) => (
        <div key={group.label}>
          <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-2">
            {group.label}
          </p>
          <div className="flex flex-wrap gap-2">
            {group.keys.map((key) => (
              <button
                key={key}
                type="button"
                title={PERMISSION_HINTS[key] || ''}
                onClick={() => toggle(key)}
                className={`px-[11px] py-[7px] rounded-[9px] text-[12.5px] font-semibold border transition ${
                  permissions[key]
                    ? 'bg-navy text-white border-navy'
                    : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-[#9CA3AF]'
                }`}
              >
                {PERMISSION_LABELS[key]}
              </button>
            ))}
          </div>
          {group.label === 'Itinerary' && (
            <p className="text-[11.5px] text-[#9CA3AF] mt-1.5">Suppliers always see the activities they're the supplier on, their bookings and approval requests.</p>
          )}
        </div>
      ))}
      {Object.entries(permissions).some(([k, on]) => on && PERMISSION_HINTS[k]) && (
        <ul className="text-[11.5px] text-[#6B7280] space-y-0.5">
          {Object.entries(permissions).filter(([k, on]) => on && PERMISSION_HINTS[k]).map(([k]) => (
            <li key={k}><span className="font-semibold text-[#374151]">{PERMISSION_LABELS[k]}:</span> {PERMISSION_HINTS[k]}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const DOC_TYPE_LABELS = {
  insurance: 'Insurance',
  health_safety: 'Health & Safety',
  hygiene_cert: 'Hygiene Certificate',
  pool_maintenance: 'Pool Maintenance',
  fire_escape: 'Fire Escape Plan',
  other: 'Other',
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [editingDocs, setEditingDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [rebuilding, setRebuilding] = useState('');
  const { isAdmin, currentUser } = useAuth();
  const loginEmails = useSupplierLoginEmails();

  async function rebuildPortalAccess() {
    setRebuilding('Rebuilding…');
    try {
      const res = await rebuildAllSupplierViews(currentUser);
      setRebuilding(`Done: ${res.views} supplier views across ${res.tours} tours.`);
    } catch (err) {
      console.error(err);
      setRebuilding('Rebuild failed. Please try again.');
    }
  }

  useEffect(() => {
    const q = query(collection(db, 'suppliers'), orderBy('name', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setSuppliers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'supplierCategories'), orderBy('order', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setServiceTypes(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.active !== false)
          .map((c) => c.name)
      );
    });
    return unsub;
  }, []);

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function toggleServiceType(type) {
    setForm((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(type)
        ? prev.serviceTypes.filter((t) => t !== type)
        : [...prev.serviceTypes, type],
    }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }
    if (form.serviceTypes.length === 0) {
      setError('Select at least one activity.');
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'suppliers'), {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        googleLink: form.googleLink.trim(),
        locationName: form.locationName.trim(),
        serviceTypes: form.serviceTypes,
        notes: form.notes.trim(),
        permissions: form.permissions,
        category: form.category,
        emailLanguage: form.emailLanguage === 'en' ? 'en' : 'es',
        createdAt: serverTimestamp(),
      });
      setForm(emptyForm);
      setShowForm(false);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function toggleEditServiceType(type) {
    setEditForm((prev) => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(type)
        ? prev.serviceTypes.filter((t) => t !== type)
        : [...prev.serviceTypes, type],
    }));
  }

  async function startEdit(supplier) {
    setEditingId(supplier.id);
    setEditForm({
      name: supplier.name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      googleLink: supplier.googleLink || '',
      locationName: supplier.locationName || '',
      serviceTypes: supplier.serviceTypes || [],
      notes: supplier.notes || '',
      permissions: effectivePermissions(supplier),
      emailLanguage: supplier.emailLanguage === 'en' ? 'en' : 'es',
      category: supplier.category || '',
    });
    setEditingDocs([]);
    setLoadingDocs(true);
    try {
      const snap = await getDocs(collection(db, 'suppliers', supplier.id, 'documents'));
      setEditingDocs(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDocs(false);
    }
  }

  async function handleUpdate(e) {
    e.preventDefault();
    if (!editForm.name.trim() || !editForm.email.trim()) return;
    if (editForm.serviceTypes.length === 0) { setError('Select at least one activity.'); return; }
    setError('');
    setSaving(true);
    try {
      await updateDoc(doc(db, 'suppliers', editingId), {
        name: editForm.name.trim(),
        email: editForm.email.trim().toLowerCase(),
        phone: editForm.phone.trim(),
        address: editForm.address.trim(),
        googleLink: editForm.googleLink.trim(),
        locationName: editForm.locationName.trim(),
        serviceTypes: editForm.serviceTypes,
        notes: editForm.notes.trim(),
        permissions: editForm.permissions,
        category: editForm.category,
        emailLanguage: editForm.emailLanguage === 'en' ? 'en' : 'es',
      });
      setEditingId(null);
    } catch (err) {
      console.error(err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remove this supplier from the directory?')) return;
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, 'suppliers', id));
    } finally {
      setDeletingId(null);
    }
  }

  const editingSupplier = suppliers.find((s) => s.id === editingId);
  const unsavedPermissions = !!editingSupplier
    && JSON.stringify(editForm.permissions) !== JSON.stringify(effectivePermissions(editingSupplier));

  return (
    <div className="space-y-5">
      {/* Admin preview of the supplier portal, with the permissions as currently set in the form (saved or not). */}
      {previewing && editingSupplier && (
        <Modal open onClose={() => setPreviewing(false)} title={`Supplier portal preview: ${editingSupplier.name}`} maxWidth="max-w-5xl">
          <div className="p-4 overflow-y-auto">
            {unsavedPermissions && (
              <p className="text-[12px] text-[#92400E] bg-[#FFFBEB] border border-[#FCD34D] rounded-[9px] px-3 py-2 mb-3">
                Showing your unsaved permission changes. Save to apply them to the supplier.
              </p>
            )}
            <PortalPreviewFrame viewer={supplierViewer(editingSupplier, { permissionsOverride: unsavedPermissions ? editForm.permissions : null })} />
          </div>
        </Modal>
      )}
      {/* ── Page header ── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">Suppliers</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">
            {suppliers.length > 0
              ? `${suppliers.length} supplier${suppliers.length !== 1 ? 's' : ''} in the directory`
              : 'Your directory of regular suppliers, ready to assign to any tour.'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={rebuildPortalAccess}
                disabled={rebuilding === 'Rebuilding…'}
                title="Rebuilds what every supplier can see in their portal. It updates by itself as tours change; use this after switching it on or if something looks out of date."
                className="text-[12.5px] font-semibold text-pb border border-pb/30 bg-white hover:bg-blue-light px-[12px] py-[9px] rounded-[9px] transition disabled:opacity-60 whitespace-nowrap"
              >
                Rebuild supplier portal access
              </button>
            )}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-pb hover:bg-navy text-white text-[13px] font-bold px-[16px] py-[10px] rounded-[9px] transition shadow-sm whitespace-nowrap"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add supplier
          </button>
        )}
          </div>
          {rebuilding && <p className="text-[11.5px] text-[#6B7280]">{rebuilding}</p>}
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
          <h3 className="text-[14.5px] font-bold text-[#111827] mb-5">New Supplier</h3>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">
                  Name <span className="text-dr">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Barcelona Hotels Ltd"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">
                  Email <span className="text-dr">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="contact@supplier.com"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Notes</label>
                <input
                  type="text"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Location name</label>
                <input
                  type="text"
                  name="locationName"
                  value={form.locationName}
                  onChange={handleChange}
                  placeholder="e.g. Elche CF Training Ground"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Address</label>
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="e.g. Calle Mayor 12, 03201 Elche, Spain"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Google Maps link</label>
                <input
                  type="url"
                  name="googleLink"
                  value={form.googleLink}
                  onChange={handleChange}
                  placeholder="https://maps.google.com/..."
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-2">
                Activities <span className="text-dr">*</span>
              </label>
              {serviceTypes.length === 0 ? (
                <p className="text-[12px] text-[#9CA3AF]">
                  No activities configured yet —{' '}
                  <a href="/staff/admin-settings" className="text-pb hover:underline font-semibold">add them in Admin Settings</a>.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {serviceTypes.map((type) => (
                    <button key={type} type="button" onClick={() => toggleServiceType(type)}
                      className={`px-[11px] py-[7px] rounded-[9px] text-[12.5px] font-semibold border transition ${
                        form.serviceTypes.includes(type) ? 'bg-navy text-white border-navy' : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-[#9CA3AF]'
                      }`}>
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <EmailLanguageField value={form.emailLanguage} onChange={(v) => setForm((p) => ({ ...p, emailLanguage: v }))} />

            <div className="border-t border-[#F1F3F6] pt-5">
              <p className="text-[13px] font-bold text-[#111827] mb-3">Portal Permissions</p>
              <PermissionsEditor
                permissions={form.permissions}
                onChange={(p) => setForm((prev) => ({ ...prev, permissions: p }))}
                category={form.category}
                onCategoryChange={(c) => setForm((prev) => ({ ...prev, category: c }))}
              />
            </div>

            {error && <p className="text-[12px] text-dr">{error}</p>}

            <div className="flex gap-2">
              <button type="submit" disabled={saving}
                className="px-[18px] py-[10px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                {saving ? 'Saving…' : 'Save supplier'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); setError(''); }}
                className="px-[18px] py-[10px] text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Suppliers list */}
      {loading ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
          <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-12 text-center">
          <p className="text-[14px] font-bold text-[#111827] mb-1">No suppliers yet</p>
          <p className="text-[13px] text-[#9CA3AF]">Add one above to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-surface border-b border-[#E5E7EB]">
                  {['Supplier', 'Contact', 'Address', 'Activities', 'Notes', ''].map((h, i) => (
                    <th key={i} className="px-[18px] py-[10px] text-left text-[10.5px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.5px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <>
                    <tr key={s.id} className="border-b border-[#F1F3F6] hover:bg-surface transition-colors">
                      <td className="px-[18px] py-[12px]">
                        <p className="font-bold text-[13px] text-[#111827]">{s.name}</p>
                        {s.locationName && <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">{s.locationName}</p>}
                      </td>
                      <td className="px-[18px] py-[12px]">
                        <p className="text-[13px] text-[#374151]">{s.email} <span className="text-[10.5px] font-bold text-[#9CA3AF]" title="Email language">{s.emailLanguage === 'en' ? 'EN' : 'ES'}</span></p>
                        {loginEmails && (loginEmails.has((s.email || '').toLowerCase())
                          ? <p className="text-[11px] font-semibold text-sg">Has portal login</p>
                          : <p className="text-[11px] font-semibold text-[#D97706]" title="Invite them from Users with the supplier role, using this email">No portal login</p>)}
                        {s.phone && <p className="text-[12px] text-[#9CA3AF]">{s.phone}</p>}
                      </td>
                      <td className="px-[18px] py-[12px]">
                        {s.address ? <p className="text-[12px] text-[#374151]">{s.address}</p> : <span className="text-[#D1D5DB]">—</span>}
                        {s.googleLink && (
                          <a href={s.googleLink} target="_blank" rel="noopener noreferrer"
                            className="text-[11px] font-semibold text-pb hover:text-navy transition">Maps ↗</a>
                        )}
                      </td>
                      <td className="px-[18px] py-[12px]">
                        <div className="flex flex-wrap gap-[5px]">
                          {(s.serviceTypes || []).map((t) => (
                            <span key={t} className="text-[11px] font-semibold bg-blue-light text-pb px-[8px] py-[3px] rounded-[20px]">{t}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-[18px] py-[12px] text-[#6B7280] text-[12.5px] max-w-[180px]">
                        {s.notes || <span className="text-[#D1D5DB]">—</span>}
                      </td>
                      <td className="px-[18px] py-[12px] text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => editingId === s.id ? setEditingId(null) : startEdit(s)}
                            className="text-[12.5px] font-bold text-pb hover:text-navy transition"
                          >
                            {editingId === s.id ? 'Close' : 'Edit'}
                          </button>
                          <button
                            onClick={() => handleDelete(s.id)}
                            disabled={deletingId === s.id}
                            className="text-[12.5px] font-bold text-dr/60 hover:text-dr transition disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>

                    {editingId === s.id && (
                      <tr key={`${s.id}-edit`} className="bg-surface border-b border-[#F1F3F6]">
                        <td colSpan={6} className="px-[18px] py-5">
                          <form onSubmit={handleUpdate} className="space-y-5">
                            {/* Basic fields */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {[
                                { label: 'Name', key: 'name', type: 'text', required: true },
                                { label: 'Email', key: 'email', type: 'email', required: true },
                                { label: 'Phone', key: 'phone', type: 'tel' },
                                { label: 'Notes', key: 'notes', type: 'text' },
                                { label: 'Location name', key: 'locationName', type: 'text', placeholder: 'e.g. Elche CF Training Ground' },
                              ].map(({ label, key, type, required, placeholder }) => (
                                <div key={key}>
                                  <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">
                                    {label}{required && <span className="text-dr ml-0.5">*</span>}
                                  </label>
                                  <input
                                    type={type}
                                    value={editForm[key]}
                                    onChange={(e) => setEditForm((p) => ({ ...p, [key]: e.target.value }))}
                                    placeholder={placeholder}
                                    className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                                  />
                                </div>
                              ))}
                              <div className="sm:col-span-2">
                                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Address</label>
                                <input
                                  type="text"
                                  value={editForm.address}
                                  onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))}
                                  placeholder="e.g. Calle Mayor 12, 03201 Elche, Spain"
                                  className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Google Maps link</label>
                                <input
                                  type="url"
                                  value={editForm.googleLink}
                                  onChange={(e) => setEditForm((p) => ({ ...p, googleLink: e.target.value }))}
                                  placeholder="https://maps.google.com/..."
                                  className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                                />
                              </div>
                            </div>

                            {/* Activities */}
                            <div>
                              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-2">
                                Activities <span className="text-dr">*</span>
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {serviceTypes.map((type) => (
                                  <button key={type} type="button" onClick={() => toggleEditServiceType(type)}
                                    className={`px-[11px] py-[7px] rounded-[9px] text-[12.5px] font-semibold border transition ${
                                      editForm.serviceTypes.includes(type) ? 'bg-navy text-white border-navy' : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-[#9CA3AF]'
                                    }`}>
                                    {type}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <EmailLanguageField value={editForm.emailLanguage} onChange={(v) => setEditForm((p) => ({ ...p, emailLanguage: v }))} />

                            {/* Permissions */}
                            <div className="border-t border-[#E5E7EB] pt-5">
                              <div className="flex items-center justify-between gap-3 mb-3">
                                <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px]">Portal Permissions</p>
                                {isAdmin && (
                                  <button type="button" onClick={() => setPreviewing(true)}
                                    className="px-3 py-[6px] text-[12px] font-bold text-pb border border-pb/30 bg-white hover:bg-blue-light rounded-[8px] transition">
                                    Preview supplier portal
                                  </button>
                                )}
                              </div>
                              <PermissionsEditor
                                permissions={editForm.permissions}
                                onChange={(p) => setEditForm((prev) => ({ ...prev, permissions: p }))}
                                category={editForm.category}
                                onCategoryChange={(c) => setEditForm((prev) => ({ ...prev, category: c }))}
                              />
                            </div>

                            {/* Compliance documents (read-only) */}
                            <div className="border-t border-[#E5E7EB] pt-5">
                              <p className="text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-3">Compliance Documents</p>
                              {loadingDocs ? (
                                <p className="text-[12px] text-[#9CA3AF]">Loading…</p>
                              ) : editingDocs.length === 0 ? (
                                <p className="text-[12px] text-[#9CA3AF]">No documents uploaded yet. Suppliers can upload these from their portal.</p>
                              ) : (
                                <div className="flex flex-col gap-2">
                                  {editingDocs.map((d) => (
                                    <div key={d.id} className="flex items-center justify-between bg-white border border-[#E5E7EB] rounded-[9px] px-4 py-[10px]">
                                      <div>
                                        <p className="text-[13px] font-semibold text-[#111827]">{d.name}</p>
                                        <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">
                                          {DOC_TYPE_LABELS[d.type] || d.type}
                                          {d.uploadedAt?.toDate && ` · ${d.uploadedAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                                        </p>
                                      </div>
                                      <a
                                        href={d.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[12.5px] font-bold text-pb hover:text-navy transition whitespace-nowrap ml-4"
                                      >
                                        Download ↗
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {error && <p className="text-[12px] text-dr">{error}</p>}
                            <div className="flex gap-2">
                              <button type="submit" disabled={saving}
                                className="px-[18px] py-[10px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                                {saving ? 'Saving…' : 'Save changes'}
                              </button>
                              <button type="button" onClick={() => setEditingId(null)}
                                className="px-4 py-[10px] text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] transition">
                                Cancel
                              </button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
